import { APIClient } from '../../helpers/api_helper';
import { CommunityVideosResponse, CommunityVideosParams, VideoCategory, VideoStatus } from './types';
import secureApi from '../../utils/secureApi';

const api = new APIClient();

export class CommunityService {
  /**
   * Get community videos by category
   * @param params - Query parameters including page, limit, and category
   */
  async getVideos(params: CommunityVideosParams, forceRefresh: boolean = false): Promise<CommunityVideosResponse> {
    try {
      const queryParams: any = {
        page: params.page || 1,
        limit: params.limit || 20
      };
      
      // Only add cache-busting timestamp when explicitly requested (forceRefresh)
      // This prevents unnecessary cache invalidation and reduces request count
      if (forceRefresh) {
        queryParams._t = Date.now();
      }
      
      if (params.category) {
        queryParams.category = params.category;
      }
      
      if (params.status) {
        queryParams.status = params.status;
      }
      
      // Add search parameter
      if (params.search && params.search.trim()) {
        queryParams.search = params.search.trim();
      }
      
      // Add tags parameter (comma-separated string or array)
      if (params.tags) {
        if (Array.isArray(params.tags)) {
          queryParams.tags = params.tags.join(',');
        } else {
          queryParams.tags = params.tags;
        }
      }
      
      // Add featured parameter
      if (params.featured !== undefined) {
        queryParams.featured = params.featured;
      }
      
      // Add uploadedBy parameter
      if (params.uploadedBy) {
        queryParams.uploadedBy = params.uploadedBy;
      }
      
      // Add sortBy parameter (default: createdAt)
      queryParams.sortBy = params.sortBy || 'createdAt';
      
      // Add sortOrder parameter (default: desc)
      queryParams.sortOrder = params.sortOrder || 'desc';
      
      // The axios interceptor returns response.data, so we cast it to our expected type
      const response = await api.get('/api/v1/community/videos', queryParams);
      
      // Handle different response structures
      if (response && (response as any).statusCode && (response as any).data) {
        return response as unknown as CommunityVideosResponse;
      } else if (response && (response as any).data) {
        // Response wrapped in data field
        return (response as any).data as unknown as CommunityVideosResponse;
      } else {
        // Direct response structure
        return response as unknown as CommunityVideosResponse;
      }
    } catch (error: any) {
      console.error('Error fetching community videos:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Handle different error types
      if (error.response?.status === 404) {
        throw new Error('Community videos not found');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please login again.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to view community videos.');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to fetch community videos');
      }
    }
  }

  /**
   * Get videos for a specific category
   * @param category - Video category (action, level_up, social)
   * @param page - Page number
   * @param limit - Items per page
   * @param status - Optional video status filter
   */
  /**
   * Get videos for a specific category
   * @param category - Video category (action, level_up, social)
   * @param page - Page number
   * @param limit - Items per page
   * @param status - Optional video status filter
   * @param search - Optional search query
   * @param tags - Optional tags filter (array or comma-separated string)
   * @param featured - Optional featured filter (boolean)
   * @param forceRefresh - Force refresh cache
   */
  async getVideosByCategory(
    category: VideoCategory,
    page: number = 1,
    limit: number = 20,
    status?: VideoStatus | string,
    search?: string,
    tags?: string | string[],
    featured?: boolean,
    forceRefresh: boolean = false
  ): Promise<CommunityVideosResponse> {
    return this.getVideos({ 
      category, 
      page, 
      limit, 
      status, 
      search, 
      tags, 
      featured 
    }, forceRefresh);
  }

  /**
   * Search videos with filters
   * @param searchQuery - Search query string
   * @param category - Video category (action, level_up, social)
   * @param tags - Optional tags filter (comma-separated string or array)
   * @param page - Page number
   * @param limit - Items per page
   * @param status - Optional status filter (published, archived, scheduled, draft, rejected)
   * @param featured - Optional featured filter (true, false, or undefined for all)
   */
  async searchVideos(
    searchQuery: string,
    category: VideoCategory,
    tags?: string | string[],
    page: number = 1,
    limit: number = 20,
    status?: VideoStatus | string,
    featured?: boolean | string
  ): Promise<CommunityVideosResponse> {
    try {
      const queryParams: any = {
        q: searchQuery.trim(), // Backend expects 'q' instead of 'searchQuery'
        category: category,
        page: page,
        limit: limit
      };

      // Add status if provided (let backend handle filtering)
      if (status) {
        queryParams.status = status;
      }

      // Add tags if provided
      if (tags) {
        if (Array.isArray(tags)) {
          queryParams.tags = tags.join(',');
        } else {
          queryParams.tags = tags;
        }
      }

      // Add featured filter if provided
      if (featured !== undefined && featured !== 'all') {
        queryParams.featured = featured === true || featured === 'true';
      }

      const response = await api.get('/api/v1/community/videos/search', queryParams);

      // Handle different response structures
      let responseData: any;
      if (response && (response as any).statusCode && (response as any).data) {
        responseData = (response as any).data;
      } else if (response && (response as any).data) {
        responseData = (response as any).data;
      } else {
        responseData = response;
      }

      // Transform search API response structure to match expected format
      // Search API returns: { categories: [{ category: "action", content: [...] }], total, page, totalPages }
      // Expected format: { videos: [...], total, page, totalPages }
      if (responseData && responseData.categories && Array.isArray(responseData.categories) && responseData.categories.length > 0) {
        // Extract videos from the first category's content array
        const videos = responseData.categories[0].content || [];
        return {
          statusCode: 200,
          status: 'success',
          message: 'Videos searched successfully',
          data: {
            videos: videos,
            total: responseData.total || 0,
            page: responseData.page?.toString() || '1',
            totalPages: responseData.totalPages || 1
          }
        } as CommunityVideosResponse;
      }

      // Fallback to original structure if it doesn't match search API format
      return responseData as unknown as CommunityVideosResponse;
    } catch (error: any) {
      console.error('Error searching community videos:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to search community videos');
      }
    }
  }

  /**
   * Generate presigned URLs for video and thumbnail upload
   * @param videoContentType - Video content type (e.g., "mp4")
   * @param thumbnailContentType - Thumbnail content type (e.g., "png")
   */
  async generateUploadUrls(
    videoContentType: string,
    thumbnailContentType: string
  ): Promise<{
    videoId: string;
    videoUploadUrl: string;
    thumbnailUploadUrl: string;
    videoKey?: string;
    thumbnailKey?: string;
  }> {
    try {
      console.log('Calling generate-upload-urls API with:', {
        videoContentType,
        thumbnailContentType
      });
      
      const response = await secureApi.post<any>(
        '/api/v1/community/video/generate-upload-urls',
        {
          videoContentType,
          thumbnailContentType
        }
      );

      console.log('generate-upload-urls API response:', response);

      // Handle different response structures
      if (response && (response as any).data) {
        const data = (response as any).data;
        return {
          videoId: data.videoId,
          videoUploadUrl: data.videoUploadUrl || data.videoPresignedUrl,
          thumbnailUploadUrl: data.thumbnailUploadUrl || data.thumbnailPresignedUrl,
          videoKey: data.videoKey || data.videoS3Key,
          thumbnailKey: data.thumbnailKey || data.thumbnailS3Key
        };
      } else if (response && (response as any).videoId) {
        return {
          videoId: (response as any).videoId,
          videoUploadUrl: (response as any).videoUploadUrl || (response as any).videoPresignedUrl,
          thumbnailUploadUrl: (response as any).thumbnailUploadUrl || (response as any).thumbnailPresignedUrl,
          videoKey: (response as any).videoKey || (response as any).videoS3Key,
          thumbnailKey: (response as any).thumbnailKey || (response as any).thumbnailS3Key
        };
      } else {
        console.error('Invalid response structure from generate-upload-urls:', response);
        throw new Error('Invalid response structure from generate-upload-urls');
      }
    } catch (error: any) {
      console.error('Error generating upload URLs:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to generate upload URLs');
      }
    }
  }

  /**
   * Upload file to S3 using presigned URL
   * @param presignedUrl - Presigned URL from generateUploadUrls
   * @param file - File to upload
   * @param contentType - Content type of the file
   */
  async uploadToS3(
    presignedUrl: string,
    file: File,
    contentType: string
  ): Promise<void> {
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType
        },
        body: file
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`S3 upload failed: ${response.status} - ${errorText}`);
      }
    } catch (error: any) {
      console.error('Error uploading to S3:', error);
      if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to upload file to S3');
      }
    }
  }

  /**
   * Complete video upload after files are uploaded to S3
   * @param payload - Video upload payload
   */
  async completeUpload(payload: any): Promise<any> {
    try {
      const response = await secureApi.post<any>(
        '/api/v1/community/video/complete-upload',
        payload
      );

      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error completing upload:', error);
      console.error('Error response data:', error.response?.data);
      
      // API returns error in 'error' field: { statusCode: 500, error: "Failed to complete video upload: Invalid metadata for action category" }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to complete upload');
      }
    }
  }

  /**
   * Get existing tags from the community
   */
  async getTags(): Promise<string[]> {
    try {
      const response = await secureApi.get<any>('/api/v1/community/tags');
      
      // Handle different response structures
      if (response && (response as any).data) {
        const data = (response as any).data;
        if (Array.isArray(data)) {
          return data;
        } else if (data.tags && Array.isArray(data.tags)) {
          return data.tags;
        } else if (data.data && Array.isArray(data.data)) {
          return data.data;
        }
      } else if (Array.isArray(response)) {
        return response;
      }
      
      return [];
    } catch (error: any) {
      console.error('Error fetching tags:', error);
      // Return empty array on error, don't block the form
      return [];
    }
  }

  /**
   * Delete a community video
   * @param videoId - Video ID to delete
   */
  /**
   * Reject/Unpublish a community video
   * @param videoId - Video ID to reject/unpublish
   */
  async rejectVideo(videoId: string): Promise<any> {
    try {
      const response = await secureApi.post<any>(`/api/v1/community/video/${videoId}/reject`);
      
      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error rejecting/unpublishing video:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to reject/unpublish video');
      }
    }
  }

  /**
   * Publish a community video
   * @param videoId - Video ID to publish
   */
  async publishVideo(videoId: string): Promise<any> {
    try {
      const response = await secureApi.post<any>(`/api/v1/community/video/${videoId}/publish`);
      
      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error publishing video:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to publish video');
      }
    }
  }

  /**
   * Publish a series
   * @param seriesId - Series ID to publish
   */
  async publishSeries(seriesId: string): Promise<any> {
    try {
      const response = await secureApi.post<any>(`/api/v1/community/series/${seriesId}/publish`);
      
      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error publishing series:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to publish series');
      }
    }
  }

  /**
   * Unpublish a series
   * @param seriesId - Series ID to unpublish
   */
  async unpublishSeries(seriesId: string): Promise<any> {
    try {
      const response = await secureApi.post<any>(`/api/v1/community/series/${seriesId}/reject`);
      
      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error unpublishing series:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to unpublish series');
      }
    }
  }

  async deleteVideo(videoId: string): Promise<any> {
    try {
      const response = await secureApi.delete<any>(`/api/v1/community/video/${videoId}`);
      return response;
    } catch (error: any) {
      console.error('Error deleting video:', error);
      throw error;
    }
  }

  /**
   * Delete a series
   * @param seriesId - Series ID
   */
  async deleteSeries(seriesId: string): Promise<any> {
    try {
      const response = await secureApi.delete<any>(`/api/v1/community/series/${seriesId}`);
      return response;
    } catch (error: any) {
      console.error('Error deleting series:', error);
      throw error;
    }
  }

  /**
   * Archive a series (used for Level Up series unpublish/delete)
   * @param seriesId - Series ID to archive
   */
  async archiveSeries(seriesId: string): Promise<any> {
    try {
      const response = await secureApi.post<any>(`/api/v1/community/series/${seriesId}/archive`);
      
      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error archiving series:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to archive series');
      }
    }
  }

  /**
   * Get all players for admin (for player selection in action category)
   * @param search - Search term to filter players
   */
  async getAllPlayersForAdmin(search: string = ''): Promise<any[]> {
    try {
      console.log('Fetching players with search:', search);
      const response = await secureApi.get<any>(
        '/api/v1/users',
        {
          params: { search, page: 1, pageSize: 100 }
        }
      );

      console.log('Players API response:', response);

      // Handle different response structures
      if (response && (response as any).data) {
        const data = (response as any).data;
        // Check for nested data.data structure (response.data.data)
        if (data.data && Array.isArray(data.data)) {
          return data.data;
        } else if (data.paginatedResults && Array.isArray(data.paginatedResults)) {
          return data.paginatedResults;
        } else if (data.players && Array.isArray(data.players)) {
          return data.players;
        } else if (Array.isArray(data)) {
          return data;
        }
      } else if ((response as any).paginatedResults && Array.isArray((response as any).paginatedResults)) {
        return (response as any).paginatedResults;
      } else if (Array.isArray(response)) {
        return response;
      }
      
      console.warn('Unexpected players API response structure:', response);
      return [];
    } catch (error: any) {
      console.error('Error fetching players:', error);
      // Return empty array on error, don't block the form
      return [];
    }
  }

  /**
   * Get all coaches for admin (for coach selection in action category)
   * @param search - Search term to filter coaches
   */
  async getAllCoachesForAdmin(search: string = ''): Promise<any[]> {
    try {
      console.log('Fetching coaches with search:', search);
      const response = await secureApi.get<any>(
        '/api/v1/coach',
        {
          params: { name: search, page: 1, pageSize: 100 }
        }
      );

      console.log('Coaches API response:', response);

      // Handle different response structures
      if (response && (response as any).data) {
        const data = (response as any).data;
        // Check for nested data.data structure (response.data.data)
        if (data.data && Array.isArray(data.data)) {
          return data.data;
        } else if (data.paginatedResults && Array.isArray(data.paginatedResults)) {
          return data.paginatedResults;
        } else if (data.coaches && Array.isArray(data.coaches)) {
          return data.coaches;
        } else if (Array.isArray(data)) {
          return data;
        }
      } else if ((response as any).paginatedResults && Array.isArray((response as any).paginatedResults)) {
        return (response as any).paginatedResults;
      } else if (Array.isArray(response)) {
        return response;
      }
      
      console.warn('Unexpected coaches API response structure:', response);
      return [];
    } catch (error: any) {
      console.error('Error fetching coaches:', error);
      // Return empty array on error, don't block the form
      return [];
    }
  }

  /**
   * Get all academies for admin (for academy selection in action category)
   * @param search - Search term to filter academies
   */
  async getAllAcademiesForAdmin(search: string = ''): Promise<any[]> {
    try {
      console.log('Fetching academies with search:', search);
      const response = await secureApi.get<any>(
        '/api/v1/academy',
        {
          params: { search, page: 1, pageSize: 100 }
        }
      );

      console.log('Academies API response:', response);

      // Handle different response structures
      if (response && (response as any).data) {
        const data = (response as any).data;
        // Check for nested data.data structure (response.data.data)
        if (data.data && Array.isArray(data.data)) {
          return data.data;
        } else if (data.paginatedResults && Array.isArray(data.paginatedResults)) {
          return data.paginatedResults;
        } else if (data.academies && Array.isArray(data.academies)) {
          return data.academies;
        } else if (Array.isArray(data)) {
          return data;
        }
      } else if ((response as any).paginatedResults && Array.isArray((response as any).paginatedResults)) {
        return (response as any).paginatedResults;
      } else if (Array.isArray(response)) {
        return response;
      }
      
      console.warn('Unexpected academies API response structure:', response);
      return [];
    } catch (error: any) {
      console.error('Error fetching academies:', error);
      // Return empty array on error, don't block the form
      return [];
    }
  }

  /**
   * Generate presigned URL for image post upload
   * @param imageContentType - Image content type (e.g., "png", "jpg")
   */
  async generateImagePostUploadUrl(imageContentType: string): Promise<{
    imageId: string;
    imageUploadUrl: string;
    imageKey?: string;
  }> {
    try {
      console.log('Calling generate-upload-url API for image post with:', {
        imageContentType
      });
      
      const response = await secureApi.post<any>(
        '/api/v1/community/image-post/generate-upload-url',
        {
          imageContentType
        }
      );

      console.log('generate-upload-url API response for image post:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', response ? Object.keys(response) : 'null');

      // Handle different response structures
      // secureApi.post returns response.data, so response is already the axios response.data
      // API returns: { statusCode: 200, status: "success", data: { postId, imageUploadUrl, imageKey, ... } }
      let imageId: string | undefined;
      let imageUploadUrl: string | undefined;
      let imageKey: string | undefined;

      // Check for nested data structure (response.data)
      if (response && (response as any).data) {
        const data = (response as any).data;
        // API uses postId instead of imageId
        imageId = data.postId || data.imageId || data.id;
        imageUploadUrl = data.imageUploadUrl || data.imagePresignedUrl || data.presignedUrl;
        imageKey = data.imageKey || data.imageS3Key || data.s3Key;
      } else if (response && typeof response === 'object') {
        // Direct response structure (fallback)
        imageId = (response as any).postId || (response as any).imageId || (response as any).id;
        imageUploadUrl = (response as any).imageUploadUrl || (response as any).imagePresignedUrl || (response as any).presignedUrl;
        imageKey = (response as any).imageKey || (response as any).imageS3Key || (response as any).s3Key;
      }

      console.log('Extracted values:', { imageId, imageUploadUrl, imageKey });

      if (imageId && imageUploadUrl) {
        return {
          imageId,
          imageUploadUrl,
          imageKey
        };
      } else {
        console.error('Invalid response structure from generate-upload-url for image post:', {
          fullResponse: response,
          extractedImageId: imageId,
          extractedImageUploadUrl: imageUploadUrl,
          extractedImageKey: imageKey
        });
        throw new Error(`Invalid response structure from generate-upload-url - missing postId/imageId or imageUploadUrl. Response: ${JSON.stringify(response)}`);
      }
    } catch (error: any) {
      console.error('Error generating upload URL for image post:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to generate upload URL for image post');
      }
    }
  }

  /**
   * Complete image post upload after file is uploaded to S3
   * @param payload - Image post upload payload
   */
  async completeImagePostUpload(payload: any): Promise<any> {
    try {
      const response = await secureApi.post<any>(
        '/api/v1/community/image-post/complete-upload',
        payload
      );

      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error completing image post upload:', error);
      console.error('Error response data:', error.response?.data);
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to complete image post upload');
      }
    }
  }

  /**
   * Get video details by ID
   * @param videoId - Video ID
   */
  async getVideoById(videoId: string): Promise<any> {
    try {
      const response = await secureApi.get<any>(`/api/v1/community/video/${videoId}`);
      
      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else if (response && (response as any).video) {
        return response;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error fetching video details:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to fetch video details');
      }
    }
  }

  /**
   * Update a community video
   * Uses endpoint: PUT /api/v1/community/video/:videoId
   * @param videoId - Video ID to update
   * @param payload - Update payload
   */
  async updateVideo(videoId: string, payload: any): Promise<any> {
    try {
      // API endpoint: PUT /api/v1/community/video/:videoId
      const response = await secureApi.put<any>(`/api/v1/community/video/${videoId}`, payload);
      
      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error updating video:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to update video');
      }
    }
  }

  /**
   * Generate presigned URLs for updating media files
   * Uses endpoint: POST /api/v1/community/media/:mediaId/generate-update-urls
   * @param mediaId - Media ID (video ID)
   * @param updateData - Update data with flags and content types
   */
  async generateUpdateUrls(
    mediaId: string,
    updateData: {
      updateVideo: boolean | string;
      updateThumbnail: boolean | string;
      videoContentType?: string;
      thumbnailContentType?: string;
    }
  ): Promise<{
    videoUploadUrl?: string;
    thumbnailUploadUrl?: string;
    videoKey?: string;
    thumbnailKey?: string;
  }> {
    try {
      const response = await secureApi.post<any>(
        `/api/v1/community/media/${mediaId}/generate-update-urls`,
        updateData
      );

      // Handle different response structures
      if (response && (response as any).data) {
        const data = (response as any).data;
        return {
          videoUploadUrl: data.videoUploadUrl || data.videoPresignedUrl,
          thumbnailUploadUrl: data.thumbnailUploadUrl || data.thumbnailPresignedUrl,
          videoKey: data.videoKey || data.videoS3Key,
          thumbnailKey: data.thumbnailKey || data.thumbnailS3Key
        };
      } else {
        return {
          videoUploadUrl: (response as any).videoUploadUrl || (response as any).videoPresignedUrl,
          thumbnailUploadUrl: (response as any).thumbnailUploadUrl || (response as any).thumbnailPresignedUrl,
          videoKey: (response as any).videoKey || (response as any).videoS3Key,
          thumbnailKey: (response as any).thumbnailKey || (response as any).thumbnailS3Key
        };
      }
    } catch (error: any) {
      console.error('Error generating update URLs:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to generate update URLs');
      }
    }
  }

  /**
   * Complete media update after files are uploaded to S3
   * Uses endpoint: POST /api/v1/community/media/:mediaId/complete-update
   * @param mediaId - Media ID (video ID)
   * @param updateData - Update data with flags and S3 keys (similar to complete-upload payload)
   */
  async completeUpdate(
    mediaId: string,
    updateData: {
      updatedVideo: boolean | string;
      updatedThumbnail: boolean | string;
      videoKey?: string;
      thumbnailKey?: string;
    }
  ): Promise<any> {
    try {
      const response = await secureApi.post<any>(
        `/api/v1/community/media/${mediaId}/complete-update`,
        updateData
      );

      // Handle different response structures
      // Response structure from API: { statusCode: 200, status: "success", message: "...", data: { video object } }
      // secureApi.post already extracts response.data from axios, so response is: { statusCode, status, message, data: { video object } }
      if (response && typeof response === 'object') {
        // Check if response has the nested structure with data field containing the video object
        if ((response as any).data && typeof (response as any).data === 'object') {
          // Response structure: { statusCode, status, message, data: { video object } }
          return (response as any).data;
        } else if ((response as any)._id || (response as any).contentUrl) {
          // Response is already the video object (direct structure)
          return response;
        }
      }
      
      // Fallback: return response as-is
      return response;
    } catch (error: any) {
      console.error('Error completing update:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to complete update');
      }
    }
  }

  /**
   * Get video analytics
   * Uses endpoint: GET /api/v1/community-interactions/analytics/videos/:videoId
   * @param videoId - Video ID
   * @param days - Number of days (filter)
   * @param interval - Time interval (hourly, daily, etc.)
   * @param signal - Optional AbortSignal to cancel the request
   */
  async getVideoAnalytics(videoId: string, days: number = 1, interval: string = 'hourly', signal?: AbortSignal): Promise<any> {
    try {
      const response = await secureApi.get<any>(
        `/api/v1/community-interactions/analytics/videos/${videoId}`,
        {
          params: { days, interval },
          signal // Pass abort signal to cancel request if needed
        }
      );
      
      console.log('Analytics API response:', response);
      
      // Handle different response structures
      // secureApi.get returns response.data from axios, so response is: { statusCode, status, message, data: {...} }
      // We need to extract the data field which contains: { mediaId, engagement, viewerBehavior, breakdown, trends, chartData }
      if (response && (response as any).data) {
        return (response as any).data; // Return the data field containing analytics
      } else if (response && (response as any).engagement) {
        // If data is already unwrapped (direct analytics object)
        return response;
      } else {
        return response;
      }
    } catch (error: any) {
      // Don't throw error if request was aborted (user navigated away)
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.message?.includes('aborted') || error.message?.includes('canceled')) {
        console.log('Analytics request was aborted');
        throw error; // Re-throw abort error so caller can handle it
      }
      
      console.error('Error fetching video analytics:', error);
      console.error('Error response:', error.response);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to fetch video analytics');
      }
    }
  }

  /**
   * Create a new series
   * @param payload - Series creation payload
   */
  async createSeries(payload: any): Promise<any> {
    try {
      const response = await secureApi.post<any>('/api/v1/community/series/create', payload);
      
      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error creating series:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to create series');
      }
    }
  }

  /**
   * Generate presigned URL for series thumbnail/cover image upload
   * @param imageContentType - Image content type (e.g., "png", "jpg")
   */
  async generateSeriesImageUploadUrl(imageContentType: string): Promise<{
    imageUploadUrl: string;
    imageKey?: string;
  }> {
    try {
      const response = await secureApi.post<any>(
        '/api/v1/community/series/generate-image-upload-url',
        { imageContentType }
      );
      
      if (response && (response as any).data) {
        const data = (response as any).data;
        return {
          imageUploadUrl: data.imageUploadUrl || data.presignedUrl,
          imageKey: data.imageKey || data.s3Key
        };
      } else {
        return {
          imageUploadUrl: (response as any).imageUploadUrl || (response as any).presignedUrl,
          imageKey: (response as any).imageKey || (response as any).s3Key
        };
      }
    } catch (error: any) {
      console.error('Error generating series image upload URL:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to generate image upload URL');
      }
    }
  }

  /**
   * Update an existing series
   * @param seriesId - Series ID to update
   * @param payload - Update payload
   */
  async updateSeries(seriesId: string, payload: any): Promise<any> {
    try {
      const response = await secureApi.put<any>(`/api/v1/community/series/${seriesId}`, payload);
      
      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error updating series:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to update series');
      }
    }
  }

  /**
   * Get all series with pagination and status filter
   * @param page - Page number
   * @param limit - Items per page
   * @param status - Optional status filter
   */
  async getAllSeries(
    page: number = 1,
    limit: number = 20,
    status?: VideoStatus | string,
    forceRefresh: boolean = false
  ): Promise<CommunityVideosResponse> {
    try {
      const queryParams: any = {
        page: page || 1,
        limit: limit || 20
      };
      
      // Only add cache-busting timestamp when explicitly requested (forceRefresh)
      // This prevents unnecessary cache invalidation and reduces request count
      if (forceRefresh) {
        queryParams._t = Date.now();
      }
      
      if (status) {
        // Convert VideoStatus enum to string if needed
        queryParams.status = typeof status === 'string' ? status : String(status);
      }
      
      const response = await secureApi.get<any>('/api/v1/community/series', { params: queryParams });
      
      // Transform series data to match CommunityVideo interface
      let seriesList: any[] = [];
      let total = 0;
      let totalPages = 1;
      
      if (response && (response as any).data) {
        const data = (response as any).data;
        if (data.series && Array.isArray(data.series)) {
          seriesList = data.series;
        } else if (Array.isArray(data)) {
          seriesList = data;
        }
        total = data.total || seriesList.length;
        totalPages = data.totalPages || Math.ceil(total / limit);
      } else if (Array.isArray(response)) {
        seriesList = response;
        total = response.length;
        totalPages = Math.ceil(total / limit);
      }
      
      // Transform series to CommunityVideo format
      const videos = seriesList.map((series: any) => ({
        _id: series._id,
        title: series.title,
        description: series.description,
        category: 'level_up',
        videoType: 'series',
        contentType: 'long_form',
        mediaType: 'video',
        contentUrl: series.coverImage || '',
        thumbnailUrl: series.thumbnailUrl || series.coverImage || '',
        isPortrait: false,
        metadata: {
          coachId: series.coachId,
          userName: series.coachName,
          userProfilePic: series.coachProfilePicture,
          basicInfo: {
            ...(series.coachSports ? { sports: series.coachSports } : {}),
            ...(series.coachBasicInfo || {}),
            ageCategory: series.coachBasicInfo?.ageCategory || series.coachAgeCategory || []
          }
        },
        tags: series.tags || [],
        likes: series.totalLikes || 0,
        comments: 0,
        shares: 0,
        saves: series.totalSaves || 0,
        views: series.totalViews || 0,
        status: series.status || 'published',
        featured: series.featured || false,
        uploadSource: 'admin',
        uploadedBy: series.createdBy || '',
        visibility: 'public',
        updatedAt: series.updatedAt || series.createdAt,
        createdAt: series.createdAt,
        __v: series.__v || 0,
        publishedAt: series.publishedAt,
        seriesId: series._id,
        duration: series.totalDuration || 0,
        difficultyLevel: series.difficultyLevel || 'beginner',
        totalChapters: series.totalChapters || 0
      }));
      
      return {
        statusCode: 200,
        status: 'success',
        message: 'Series fetched successfully',
        data: {
          videos,
          total,
          page: String(page),
          totalPages
        }
      };
    } catch (error: any) {
      console.error('Error fetching series:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to fetch series');
      }
    }
  }

  /**
   * Get videos-level-up with pagination and status filter
   * @param page - Page number
   * @param limit - Items per page
   * @param status - Optional status filter (backward compatibility - sets both mediaStatus and seriesStatus)
   * @param forceRefresh - Force refresh cache
   * @param search - Optional search query
   * @param tags - Optional tags filter (array of tag strings)
   * @param mediaStatus - Optional mediaStatus filter (takes precedence over status if provided)
   * @param seriesStatus - Optional seriesStatus filter (takes precedence over status if provided)
   */
  async getVideosLevelUp(
    page: number = 1,
    limit: number = 20,
    status?: VideoStatus | string,
    forceRefresh: boolean = false,
    search?: string,
    tags?: string[],
    mediaStatus?: VideoStatus | string,
    seriesStatus?: VideoStatus | string
  ): Promise<CommunityVideosResponse> {
    try {
      const queryParams: any = {
        page: page || 1,
        limit: limit || 20
      };
      
      // Only add cache-busting timestamp when explicitly requested (forceRefresh)
      if (forceRefresh) {
        queryParams._t = Date.now();
      }
      
      // Backend expects mediaStatus and seriesStatus query parameters
      // If mediaStatus or seriesStatus are provided separately, use them
      // Otherwise, fall back to using status for both (backward compatibility)
      if (mediaStatus !== undefined) {
        const mediaStatusStr = typeof mediaStatus === 'string' ? mediaStatus : String(mediaStatus);
        queryParams.mediaStatus = mediaStatusStr;
      } else if (status) {
        const statusStr = typeof status === 'string' ? status : String(status);
        queryParams.mediaStatus = statusStr;
      }
      
      if (seriesStatus !== undefined) {
        const seriesStatusStr = typeof seriesStatus === 'string' ? seriesStatus : String(seriesStatus);
        queryParams.seriesStatus = seriesStatusStr;
      } else if (status) {
        const statusStr = typeof status === 'string' ? status : String(status);
        queryParams.seriesStatus = statusStr;
      }
      
      // Add search parameter
      if (search && search.trim()) {
        queryParams.search = search.trim();
      }
      
      // Add tags parameter (comma-separated string or array)
      if (tags && tags.length > 0) {
        queryParams.tags = tags.join(',');
      }
      
      const response = await secureApi.get<any>('/api/v1/community/videos-level-up', { params: queryParams });
      
      // API returns: { statusCode, status, message, data: { content: [...], total, page, totalPages } }
      // Need to transform content array to videos array and normalize each item
      let responseData: any;
      if (response && (response as any).statusCode && (response as any).data) {
        responseData = response;
      } else if (response && (response as any).data) {
        responseData = { statusCode: 200, status: 'success', message: '', data: (response as any).data };
      } else {
        responseData = { statusCode: 200, status: 'success', message: '', data: response };
      }
      
      const content = responseData.data?.content || [];
      const total = responseData.data?.total || 0;
      const totalPages = responseData.data?.totalPages || 1;
      const pageNum = responseData.data?.page || page;
      
      // Transform content items to CommunityVideo format
      const videos = content.map((item: any) => {
        const isSeries = item.contentType === 'series';
        const engagement = item.engagement || {};
        const metadata = item.metadata || {};
        const basicInfo = metadata.basicInfo || {};
        
        // For single videos: use contentUrl/thumbnailUrl directly
        // For series: use coverImage for both contentUrl and thumbnailUrl
        const contentUrl = isSeries ? (item.coverImage || '') : (item.contentUrl || '');
        const thumbnailUrl = isSeries ? (item.coverImage || '') : (item.thumbnailUrl || item.contentUrl || '');
        
        // Duration: single videos have it directly, series have it in metadata.totalDuration
        const duration = isSeries ? (metadata.totalDuration || 0) : (item.duration || 0);
        
        return {
          _id: item._id,
          title: item.title || '',
          description: item.description || '',
          category: item.category || 'level_up',
          videoType: isSeries ? 'series' : (item.videoType || 'single'),
          contentType: item.contentType || (isSeries ? 'series' : 'long_form'),
          mediaType: item.mediaType || 'video',
          contentUrl,
          thumbnailUrl,
          isPortrait: false,
          metadata: {
            userId: metadata.id,
            userName: basicInfo.fullName,
            userProfilePic: basicInfo.profilePic || '',
            coachId: basicInfo.coachId || metadata.id,
            basicInfo: {
              fullName: basicInfo.fullName,
              profilePic: basicInfo.profilePic,
              dob: basicInfo.dob,
              email: basicInfo.email,
              sports: basicInfo.sports || [],
              ageCategory: basicInfo.ageCategory || [],
              experience: basicInfo.experience,
              location: basicInfo.location,
              certification: basicInfo.certification
            }
          },
          tags: item.tags || [],
          likes: engagement.likes || 0,
          comments: engagement.comments || 0,
          shares: engagement.shares || 0,
          saves: engagement.saves || 0,
          views: engagement.views || 0,
          status: item.status || 'published',
          featured: item.featured || false,
          trendingScore: item.trendingScore || engagement.trendingScore,
          uploadSource: 'admin',
          uploadedBy: metadata.id || basicInfo.coachId || '',
          visibility: 'public',
          updatedAt: item.updatedAt || item.publishedAt || new Date().toISOString(),
          createdAt: item.createdAt || item.publishedAt || new Date().toISOString(),
          __v: item.__v || 0,
          publishedAt: item.publishedAt,
          // Level Up specific fields
          seriesId: isSeries ? item._id : undefined,
          duration,
          difficultyLevel: metadata.difficultyLevel || 'beginner',
          totalChapters: metadata.totalChapters || 0
        };
      });
      
      return {
        statusCode: responseData.statusCode || 200,
        status: responseData.status || 'success',
        message: responseData.message || 'Level up content fetched successfully',
        data: {
          videos,
          total,
          page: String(pageNum),
          totalPages
        }
      };
    } catch (error: any) {
      console.error('Error fetching videos-level-up:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to fetch videos-level-up');
      }
    }
  }

  async getSeriesById(seriesId: string, useCacheBust: boolean = false): Promise<any> {
    try {
      const config: any = {};
      if (useCacheBust) {
        config.params = { _t: Date.now() };
      }
      const response = await secureApi.get<any>(`/api/v1/community/series/${seriesId}`, config);
      
      // The API returns: { statusCode, status, message, data: { series object } }
      // Return the full response so the caller can extract data.data
      return response;
    } catch (error: any) {
      console.error('Error fetching series details:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to fetch series details');
      }
    }
  }

  /**
   * Get chapters for a series
   * @param seriesId - Series ID
   */
  async getSeriesChapters(seriesId: string): Promise<any[]> {
    try {
      const response = await secureApi.get<any>(`/api/v1/community/series/${seriesId}/chapters`);
      
      if (response && (response as any).data) {
        const data = (response as any).data;
        if (Array.isArray(data)) {
          return data;
        } else if (data.chapters && Array.isArray(data.chapters)) {
          return data.chapters;
        } else if (data.videos && Array.isArray(data.videos)) {
          return data.videos;
        }
        return [];
      } else if (Array.isArray(response)) {
        return response;
      } else {
        return [];
      }
    } catch (error: any) {
      console.error('Error fetching series chapters:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to fetch series chapters');
      }
    }
  }

  /**
   * Sync series progress
   * @param seriesId - Series ID
   */
  async syncSeriesProgress(seriesId: string): Promise<any> {
    try {
      const response = await secureApi.post<any>(`/api/v1/community/series/${seriesId}/sync-progress`);
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error syncing series progress:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to sync series progress');
      }
    }
  }

  /**
   * Update coach details (including profile picture)
   * @param coachId - Coach ID to update
   * @param payload - Update payload with basicInfo containing profilePic
   */
  async updateCoachDetails(coachId: string, payload: { basicInfo: { profilePic: string } }): Promise<any> {
    try {
      const response = await secureApi.post<any>(
        `/api/v1/users/update-coach-details/${coachId}`,
        payload
      );
      
      // Handle different response structures
      if (response && (response as any).data) {
        return (response as any).data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error('Error updating coach details:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to update coach details');
      }
    }
  }

}

export const communityService = new CommunityService();

